/**
 * Location vocabulary — one TypeScript copy of pipeline.config.
 *
 * Authority is Python `is_valid_location_code` / `LOCATION_FIELD_PATTERN`
 * (`pipeline/config.py`). This file is the TypeScript derivation: Worker and
 * portal import from here; they must not redeclare a third (fourth) pattern.
 * OpenAPI `LedgerChangePropose.term` locksteps the anchored field pattern.
 * Writer-safety cannot be expressed in OpenAPI; Zod and this function carry it.
 *
 * Drift-checked by `tests/test_location_field_lockstep.py`.
 *
 * Recorded dependency (R35R-1): `pipeline/ai_categorizer.py` emits
 * `Locations: {', '.join(company.locations)}` unwrapped. Hub-shaped terms
 * admit `<`; wrapping that line is required before an applier writes AM-typed
 * terms into `company.yaml`. No applier exists yet — this form stores a D1
 * row, not config. Do not "fix" by slugifying: Books stores hub strings
 * verbatim (`receipt_context.ts`).
 *
 * This file must not import zod — portal imports it without the schema graph
 * (GRANT 17 pattern; hub-source-batch-id-pattern.ts).
 */

/** Legacy lowercase codes (`hq`, `cedar-lake`). Python LOCATION_CODE_PATTERN. */
export const LOCATION_CODE_PATTERN = "[a-z0-9][a-z0-9-]*";

/**
 * Hub hierarchical / freeform location strings.
 * Python HUB_LOCATION_PATTERN: at most one `:`; each segment 1-100 chars;
 * no newlines/tabs; neither segment empty or containing `:`.
 * String.raw so the source spelling locksteps the Python raw string.
 */
export const HUB_LOCATION_PATTERN =
  String.raw`[^\n\t:]{1,100}(?::[^\n\t:]{1,100})?`;

/** Combined field pattern (Python LOCATION_FIELD_PATTERN). Unanchored. */
export const LOCATION_FIELD_PATTERN = `(?:${LOCATION_CODE_PATTERN}|${HUB_LOCATION_PATTERN})`;

/** Anchored form used by OpenAPI `pattern` and Zod `.regex`. */
export const LOCATION_FIELD_PATTERN_ANCHORED = `^${LOCATION_FIELD_PATTERN}$`;

/**
 * Wire bound: hub parent:leaf is 100 + `:` + 100. Python's slug alternative
 * has no length cap; 201 is the hub structural max and the safe direction.
 */
export const LOCATION_TERM_MAX_LENGTH = 201;

const LOCATION_CODE_RE = new RegExp(`^${LOCATION_CODE_PATTERN}$`);
const HUB_LOCATION_RE = new RegExp(`^${HUB_LOCATION_PATTERN}$`);
const WRITER_SAFE_CHAR_RE = /[\p{L}\p{M}\p{N}\p{P}\p{S}\p{Z}]/u;
const CONTROL_CHAR_RE = /\p{C}/u;

function locationCharsAreWriterSafe(value: string): boolean {
  for (const ch of value) {
    if (ch === '"' || ch === "\\") {
      return false;
    }
    if (CONTROL_CHAR_RE.test(ch)) {
      return false;
    }
    if (!WRITER_SAFE_CHAR_RE.test(ch)) {
      return false;
    }
  }
  return true;
}

/**
 * True if `value` is a legacy location code or a hub location string.
 * Lockstep with `pipeline.config.is_valid_location_code`.
 */
export function isValidLocationCode(value: string): boolean {
  if (typeof value !== "string") {
    return false;
  }
  if (value !== value.trim()) {
    return false;
  }
  if (!LOCATION_CODE_RE.test(value) && !HUB_LOCATION_RE.test(value)) {
    return false;
  }
  return locationCharsAreWriterSafe(value);
}
